/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ItemList, SetupExcpParam } from 'models/setup/excp/excp';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import useSessionStore from 'stores/useSessionStore';
import { SetupProject } from 'models/setup/SetupProject';
import { hasRole } from 'utils/SessionUtil';
import { SetupSchedule } from 'models/setup/SetupSchedule';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import _ from 'lodash';
import { CommonUtil } from 'utils/CommonUtil';
import { getSetupPackingList, getProjectPackingCnt } from 'apis/setup/excp/packingList';
import { useLocation } from 'react-router-dom';
import WJCellTextarea from 'components/inputs/WJCellTextarea';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import { findDisplayProject } from 'apis/setup/SetupProject';

interface PackingParam {
  elmPrjId?: string;
  initialOnly?: string;
}

const SetupPackingListManagementPage = () => {
  const { t } = useTranslation();
  const { userId, deptCd, deptNm, elmPrjId, ensolYn } = useSessionStore();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const locationState = useLocation().state as PackingParam | null;

  const isEmailFormat = () => {
    if (!userId) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(userId);
  };

  const initSearchParam = {
    bildPlntCd: [],
    elmPrjId: elmPrjId || '',
    excpId: ensolYn === 'Y' ? '' : deptCd,
    excpNm: ensolYn === 'Y' ? '' : deptNm,
    searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
  };

  const gridRef = useRef<any>();
  const gridUiRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [excpParam, setExcpParam] = useState<SetupExcpParam>({
    ...initSearchParam,
  });
  const [rowData, setRowData] = useState<ItemList[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]); //프로젝트
  const [bildPlntCode, setBildPlntCode] = useState<Code[]>(); // 플랜트
  const [prjExcpData, setPrjExcpData] = useState<SetupSchedule[]>([]); //협력사
  const [projectCnt, setProjectCnt] = useState<SetupExcpParam>();
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);
  const [hitTest, setHitTest] = useState<any>();

  const initCodes = async () => {
    const bildPlntOption: Code[] = await getCommonCodeNames('FA_SU_PLNT_CD'); // 플랜트코드
    setBildPlntCode(bildPlntOption);

    findDisplayProject().then((result) => {
      setPrjMasterData(result || []);

      if (!excpParam.elmPrjId && result?.length > 0) {
        handleOnChange('elmPrjId', result[0].elmPrjId);
      }
    });
  };

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (excpParam?.bildPlntCds || []).length > 0
          ? excpParam.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, excpParam?.bildPlntCds]);

  const optionsPrjExcp = useMemo(() => {
    return (prjExcpData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.excpId,
          cmnCdNm: cur.excpNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [prjExcpData]);

  const getItemCnt = async () => {
    getProjectPackingCnt(excpParam).then((result: ItemList) => {
      if (result) {
        setProjectCnt(result);
      }
    });
  };

  const btnSearch = () => {
    setErrors({
      elmPrjId: _.isEmpty(excpParam.elmPrjId),
    });

    if (_.isEmpty(excpParam.elmPrjId)) {
      return;
    }
    openLoading(true);
    getItemCnt();
    getSetupPackingList({ ...excpParam })
      .then((result: ItemList[]) => {
        setExcpParam((prev) => _.omit(prev, 'initialOnly')); // 초기 검색조건 제거
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const btnReload = () => {
    const isExcp = !hasRole('FA_DOVI') && isEmailFormat();
    setExcpParam((prev) => ({
      bildPlntCd: [],
      elmPrjId: '',
      excpId: isExcp ? prev.excpId : '',
      excpNm: isExcp ? prev.excpNm : '',
      searchOwnedPrjYn: isEmailFormat() ? 'Y' : 'N',
    }));
  };

  const handleOnChange = (name, value) => {
    setExcpParam((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const layoutDefinition = [
    {
      binding: 'eqclId',
      header: String(t('com.label.설비군', '설비군')),
      width: 150,
      align: 'center',
    },
    {
      binding: 'eqpId',
      header: String(t('com.label.M-CODE', 'M-CODE')),
      width: 150,
      align: 'center',
    },
    {
      binding: 'eqpNm',
      header: String(t('com.label.설비명', '설비명')),
      width: 250,
      align: 'left',
    },
    {
      binding: 'excpNm',
      header: String(t('com.label.협력업체명', '협력업체명')),
      width: 200,
      align: 'center',
    },
    {
      header: String(t('com.label.FOB', 'FOB')),
      columns: [
        {
          binding: 'fobPlanStDt',
          header: String(t('setup.label.계획시작일', '계획시작일')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'fobPlanEndDt',
          header: String(t('setup.label.계획종료일', '계획종료일')),
          width: 150,
          align: 'center',
        },
      ],
    },
    {
      header: String(t('com.label.FABIN', 'FAB-IN')),
      columns: [
        {
          binding: 'fabinPlanStDt',
          header: String(t('setup.label.계획시작일', '계획시작일')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'fabinPlanEndDt',
          header: String(t('setup.label.계획종료일', '계획종료일')),
          width: 150,
          align: 'center',
        },
      ],
    },
    {
      binding: 'pakItemNo',
      header: String(t('com.label.품목 No', '품목 No')),
      width: 140,
      align: 'center',
    },
    {
      binding: 'pakItemNm',
      header: String(t('com.label.품목명', '품목명')),
      width: 300,
      align: 'left',
    },
    {
      binding: 'pakItemQty',
      header: String(t('com.label.수량', '수량')),
      width: 100,
      align: 'right',
      dataType: 'Number',
    },
    {
      binding: 'qtyUnitCd',
      header: String(t('com.label.단위', '단위')),
      width: 80,
      align: 'center',
    },
    {
      binding: 'pakBoxNo',
      header: String(t('com.label.Box No', 'Box No')),
      width: 140,
      align: 'center',
    },
    {
      binding: 'rfidTagUseYn',
      header: String(t('setup.label.바코드 출력', '바코드 출력')),
      width: 100,
      align: 'center',
    },
    {
      binding: 'ctnrSrno',
      header: String(t('com.label.박스 No', 'CNTR No')),
      width: 150,
      align: 'center',
    },
    {
      header: String(t('com.label.Site', 'Site')),
      columns: [
        {
          binding: 'shdsNm',
          header: String(t('com.label.Site 명', 'Site 명')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'shdsUpdEtaDt',
          header: String(t('com.label.도착 예정일', '도착 예정일')),
          width: 150,
          align: 'center',
        },
        {
          binding: 'shdsAtaDt',
          header: String(t('com.label.실제 도착일', '실제 도착일')),
          width: 150,
          align: 'center',
        },
      ],
    },
    {
      binding: 'ctnrTpCd',
      header: String(t('com.label.CNTR Type', 'CNTR Type')),
      width: 150,
      align: 'center',
    },
    {
      header: String(t('com.label.Box Size', 'Box Size')),
      columns: [
        {
          binding: 'ctnrLen',
          header: String(t('com.label.L(mm)', 'L(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrBth',
          header: String(t('com.label.W(mm)', 'W(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrHigt',
          header: String(t('com.label.H(mm)', 'H(mm)')),
          width: 150,
          align: 'center',
          dataType: 'Number',
        },
        {
          binding: 'ctnrCapa',
          header: String(t('com.label.CBM', 'CBM')),
          width: 150,
          align: 'center',
        },
      ],
    },
    {
      binding: 'dataUpdUserNm',
      header: String(t('com.label.수정자', '수정자')),
      width: 150,
      align: 'center',
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일자', '수정일자')),
      width: 150,
      align: 'center',
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;
    grid.hostElement.addEventListener('click', (e) => {
      const ht = grid.hitTest(e);
      setHitTest(ht);
      const item = grid.rows[ht.row].dataItem;
      if (item.pakItemNm) {
        switch (ht.panel.columns[ht.col].binding) {
          case 'pakItemNm': {
            setOpenCellTextarea(true);
            break;
          }
        }
      }
    });
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('setup.label.Packing List 조회', 'Packing List 조회'),
      t('setup.label.Packing List', 'Packing List')
    );
  };

  useEffect(() => {
    initCodes();
  }, []);

  useEffect(() => {
    if (excpParam?.elmPrjId) {
      findExcpByElmPrjId(excpParam?.elmPrjId).then((result: SetupSchedule[]) => {
        setPrjExcpData(result);
      });
      btnSearch();
    } else {
      setPrjExcpData([]);
    }
  }, [excpParam?.elmPrjId, excpParam?.excpId]);

  useEffect(() => {
    if (!_.isEmpty(locationState)) {
      setExcpParam({
        ...excpParam,
        elmPrjId: locationState?.elmPrjId,
        initialOnly: locationState?.initialOnly || '',
      });
    }
  }, [locationState]);

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('setup.label.플랜트', '플랜트')}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={bildPlntCode || []}
                  defaultValue={excpParam.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          excpParam?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                      if (!isEmailFormat()) handleOnChange('excpId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span className={'dot'}>{t('setup.label.프로젝트명', '프로젝트명')}</span>
                </label>
                <ComboBox
                  placeholder={String(t('com.label.선택', '선택'))}
                  options={optionsPrjMaster}
                  defaultValue={
                    locationState?.elmPrjId ? locationState?.elmPrjId : excpParam?.elmPrjId
                  }
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                    if (!isEmailFormat()) handleOnChange('excpId', '');
                  }}
                  isError={errors?.elmPrjId}
                  msgError={String(
                    t('setup.label.프로젝트명을 선택해 주세요.', '프로젝트명을 선택해 주세요.')
                  )}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.협력업체명', '협력업체명')}</label>
                {hasRole('FA_DOVI') || !isEmailFormat() ? (
                  <ComboBox
                    placeholder={String(t('com.label.전체', '전체'))}
                    options={optionsPrjExcp}
                    defaultValue={excpParam?.excpId}
                    onChange={(value) => {
                      handleOnChange('excpId', value);
                    }}
                  />
                ) : (
                  <div className="txt">
                    <span>{excpParam?.excpNm}</span>
                  </div>
                )}
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button css={IconButton.button} className="reload" disableRipple onClick={btnReload} />
            <Button css={IconButton.button} className="find" disableRipple onClick={btnSearch}>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.Packing List', 'Packing List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
          {projectCnt && (
            <div className="info warning">
              {t('setup.label.FOB 완료율(설비기준)', 'FOB 완료율(설비기준)')} :{' '}
              {projectCnt?.summary}
            </div>
          )}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <GridSettingButtons
            gridId={'packing-grid-id'}
            layoutDefinition={layoutDefinition}
            changeShownColumns={(result) => {
              gridUiRef.current?.changeColumn?.(result);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id="packing-grid-id"
        ref={gridUiRef}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        isSelector={false}
        isReadOnly={true}
        height={567}
      />
      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={flexRef}
          hitTest={hitTest}
          close={() => setOpenCellTextarea(false)}
          textAreaByteMax={4000}
          isReadOnly
        />
      )}
    </>
  );
};

export default SetupPackingListManagementPage;
